import { useEffect, useMemo, useState } from 'react';
import { concat, from, map, of, scan, type Observable } from 'rxjs';

import { useEndpointsContext } from '../contexts';
import type { OHLCData } from '../types/OHLCData';
import type { ResolutionEnumNoPrefix } from '../types/OrderAnalytic';
import { dateComparator, formattedDateForSubscription, Get, urlFromLocalOrAbsolute } from '../utils';
import { useSubscription } from './useSubscription';

interface useOHLCDataProps {
  symbol: string | undefined;
  markets?: string[];
  startDate: Date | undefined;
  endDate: Date | undefined;
  resolution: ResolutionEnumNoPrefix | undefined;
  liveUpdates?: boolean;
}

export function sortOHLCDataChronologically(data: OHLCData[]): OHLCData[] {
  return data.toSorted((a, b) => dateComparator(a.Timestamp, b.Timestamp));
}

export const useOHLCData = (
  { symbol, markets, startDate, endDate, resolution, liveUpdates = true }: useOHLCDataProps | undefined = {
    symbol: undefined,
    startDate: undefined,
    endDate: undefined,
    resolution: undefined,
  }
) => {
  const { orgApiEndpoint, apiEndpoint } = useEndpointsContext();
  const [request, setRequest] = useState<any>();
  const [observable, setObservable] = useState<Observable<OHLCData[]>>(of([]));
  const { data: sub } = useSubscription(request);

  const sd = formattedDateForSubscription(startDate);
  const ed = useMemo(() => formattedDateForSubscription(liveUpdates ? new Date() : endDate), [liveUpdates, endDate]);

  useEffect(() => {
    if (sd == null || ed == null || symbol == null || resolution == null) {
      return;
    }
    const marketsPart = markets && markets.length > 0 ? `/markets/${encodeURIComponent(markets.join(','))}` : '';

    if (liveUpdates) {
      setRequest({
        name: 'OHLCV',
        tag: `OHLCV-${symbol}-${resolution}`,
        Symbol: symbol,
        Markets: markets,
        Resolution: resolution,
      });
    }

    setObservable(
      concat(
        from(
          Get(
            // Use org API endpoint if available (for Principal), otherwise use API endpoint, this is to support the new non-native OHLC resolutions
            urlFromLocalOrAbsolute(orgApiEndpoint ?? apiEndpoint),
            `/symbols/${symbol}${marketsPart}/ohlcv/${resolution}?startDate=${sd}&endDate=${ed}`
          )
        ),
        // We only want to subscribe to the live updates if liveUpdates is true
        liveUpdates ? sub : of([])
      ).pipe(
        map(res => res.data),
        scan((prices: Map<string, OHLCData>, data: OHLCData[]) => {
          data?.forEach(newPrice => {
            // REST and WS APIs returns the timestamp timezones in different formats, so we need to convert it to ISO string to avoid issues
            prices.set(new Date(newPrice.Timestamp).toISOString(), newPrice);
          });
          return prices;
        }, new Map<string, OHLCData>()),
        map(map => sortOHLCDataChronologically(Array.from(map.values())))
      )
    );
  }, [symbol, sd, ed, resolution, sub, apiEndpoint, orgApiEndpoint, liveUpdates, markets]);

  return observable;
};
